import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UserService } from '@app/core/services/user.service';
import { UserDto, AuthorizationDto } from '@app/generated/model';
import { WorkCenter } from './work-center';
import { Project } from './project';
import { createSearchIndex, SearchIndex } from 'src/app/utils';
import { ProfileService } from './profile.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { first, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { Site } from './site';
import { Step, StepperState } from '@app/shared/stepper/stepper';
import { ConfirmComponent } from '@app/shared/confirm/confirm.component';
import { ConfirmDialogModel } from '@app/shared/confirm/confirm-dialog.model';

const SELECT_SITES = 'Select Sites';
const SELECT_WORKCENTERS = 'Select Workcenters';
const SELECT_PROJECTS = 'Select Projects';
const OVERVIEW = 'Overview';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProfileComponent implements OnInit {
  loaded: boolean;
  user: UserDto;
  currentAuthorizationProfile: AuthorizationDto;
  workCenters: WorkCenter[];
  projects: Project[];
  sites: Site[];

  steps: Step[] = [];
  searchSiteText: string;
  searchWorkCentersText: string;
  searchProjectText: string;
  workCentersIndex: SearchIndex<WorkCenter>;
  projectIndex: SearchIndex<Project>;
  siteIndex: SearchIndex<Site>;

  private destroy$: Subject<void> = new Subject();

  constructor(
    private userService: UserService,
    private profileService: ProfileService,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private router: Router,
  ) {
    this.workCenters = [];
    this.projects = [];
    this.steps.push({ title: SELECT_SITES, state: StepperState.IN_PROGRESS });
    this.steps.push({ title: SELECT_WORKCENTERS, state: StepperState.NOT_STARTED });
    this.steps.push({ title: SELECT_PROJECTS, state: StepperState.NOT_STARTED });
    this.steps.push({ title: OVERVIEW, state: StepperState.NOT_STARTED });
  }

  async ngOnInit(): Promise<void> {
    this.profileService
      .getSites()
      .pipe(takeUntil(this.destroy$))
      .subscribe((sites) => {
        this.sites = sites;
        this.siteIndex = createSearchIndex(this.sites, ['name']);
      });

    this.profileService.getWorkCenters().subscribe((workCenters) => {
      this.workCenters = workCenters;
      this.workCentersIndex = createSearchIndex(this.workCenters, ['number', 'name', 'site']);
    });

    this.profileService.getProjects().subscribe((projects) => {
      this.projects = projects;
      this.projectIndex = createSearchIndex(this.projects, ['id', 'name']);
    });

    await this.userService.load();
    this.user = this.userService.get();

    // The backend no longer exposes a single `role` or `sites` on the user.
    // Use the list of authorizations to determine the currently selected profile.
    const auths = this.user.authorizations ?? [];
    // Prefer a single authorization if present, otherwise pick the first one as a sensible default.
    this.currentAuthorizationProfile = auths.length === 1 ? auths[0] : (auths[0] ?? null);

    await this.profileService.load();

    this.loaded = true;
  }

  displayBackBtn() {
    return this.steps[0].state === StepperState.DONE;
  }

  disableNextButton() {
    if (this.isStepInProgress(SELECT_SITES) && this.getSelectedSites().length === 0) {
      return true;
    } else if (this.isStepInProgress(SELECT_WORKCENTERS) && this.getSelectedWorkCenters().length === 0) {
      return true;
    } else if (this.isStepInProgress(SELECT_PROJECTS) && this.getSelectedProjects().length === 0) {
      return true;
    }
    return false;
  }

  private isStepInProgress(title: string): boolean {
    return this.steps.find((step) => step.title === title)?.state === StepperState.IN_PROGRESS;
  }

  backButtonClicked() {
    let index;
    const step = this.steps.find((s, idx) => {
      index = idx;
      return s.state === StepperState.IN_PROGRESS;
    });
    if (step !== undefined && index !== 0) {
      this.steps[index].state = StepperState.NOT_STARTED;
      this.steps[index - 1].state = StepperState.IN_PROGRESS;
    }
  }

  async nextButtonClicked() {
    let index;
    const currentStep = this.steps.find((s, idx) => {
      index = idx;
      return s.state === StepperState.IN_PROGRESS;
    });
    if (currentStep.title === SELECT_SITES) {
      this.profileService.getWorkcentersForSite().then(() => {
        this.steps[index].state = StepperState.DONE;
        this.steps[index + 1].state = StepperState.IN_PROGRESS;
      });
    } else if (currentStep.title === SELECT_WORKCENTERS) {
      this.profileService.getProjectsForWorkCenter().then(() => {
        this.steps[index].state = StepperState.DONE;
        this.steps[index + 1].state = StepperState.IN_PROGRESS;
      });
    } else if (currentStep.title === SELECT_PROJECTS) {
      this.steps[index].state = StepperState.DONE;
      this.steps[index + 1].state = StepperState.IN_PROGRESS;
    } else if (currentStep.title === OVERVIEW) {
      await this.userService.setProfile(
        this.getSelectedSites().map((s) => s.id),
        this.getSelectedWorkCenters().map((wc) => wc.id),
        this.getSelectedProjects().map((p) => p.id),
      );
      localStorage.removeItem('selectedSites');
      localStorage.removeItem('selectedTaskNos');
      await this.router.navigateByUrl('/').then(() => window.location.reload());
    }
  }

  displaySiteList() {
    return this.isStepInProgress(SELECT_SITES);
  }

  displayWorkCenterList() {
    return this.isStepInProgress(SELECT_WORKCENTERS);
  }

  displayProjectList() {
    return this.isStepInProgress(SELECT_PROJECTS);
  }

  displayOverview() {
    return this.isStepInProgress(OVERVIEW);
  }

  clearSearchSiteText() {
    this.searchSiteText = '';
  }

  clearSearchWorkCentersText() {
    this.searchWorkCentersText = '';
  }

  clearSearchProjectText() {
    this.searchProjectText = '';
  }

  getSites(): Site[] {
    const sites = this.searchSiteText ? this.siteIndex.search(this.searchSiteText) : this.sites;
    return [...sites].sort((a, b) => a.name.localeCompare(b.name));
  }

  getWorkCenters(): WorkCenter[] {
    const workCenters = this.searchWorkCentersText
      ? this.workCentersIndex.search(this.searchWorkCentersText)
      : this.workCenters;
    return [...workCenters].sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }));
  }

  getSelectedWorkCenters(): WorkCenter[] {
    return this.workCenters.filter((wc) => wc.selected);
  }

  getSelectedProjects(): Project[] {
    return this.projects.filter((p) => p.selected);
  }

  getSelectedSites(): Project[] {
    return this.sites.filter((s) => s.selected);
  }

  getProjects(): Project[] {
    const projects = this.searchProjectText ? this.projectIndex.search(this.searchProjectText) : this.projects;
    return [...projects].sort((a, b) => a.name.localeCompare(b.name));
  }

  toggleSite(siteId: string) {
    this.profileService.toggleSite(siteId);
  }

  toggleWorkCenter(workCenterId: string) {
    this.profileService.toggleWorkCenter(workCenterId);
  }

  confirmToggleWorkCenter(workCenter: WorkCenter) {
    const message = this.translateService.instant('profile.removeWorkCenterConfirm');
    const confirmMessage = workCenter.number + ' - ' + workCenter.name;

    const dialog = this.dialog.open(ConfirmComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        message,
        confirmMessage,
        hideCancelButton: false,
      } as ConfirmDialogModel,
    });

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.toggleWorkCenter(workCenter.id);
      });
  }

  confirmToggleSite(site: Site) {
    const message = this.translateService.instant('profile.removeSiteConfirm');
    const confirmMessage = site.name;

    const dialog = this.dialog.open(ConfirmComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        message,
        confirmMessage,
        hideCancelButton: false,
      } as ConfirmDialogModel,
    });

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.toggleSite(site.id);
      });
  }

  toggleProject(projectId: string) {
    this.profileService.toggleProject(projectId);
  }

  confirmToggleProject(project: Project) {
    const message = this.translateService.instant('profile.removeProjectConfirm');
    const confirmMessage = project.name + ' (' + project.id + ')';

    const dialog = this.dialog.open(ConfirmComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        message,
        confirmMessage,
        hideCancelButton: false,
      } as ConfirmDialogModel,
    });

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.toggleProject(project.id);
      });
  }

  setAuthorizationProfile(authorization: AuthorizationDto) {
    this.profileService.setAuthorizationProfile(authorization).subscribe(async () => {
      await this.userService.load();
      this.user = this.userService.get();
      await this.profileService.load();

      // Since user.role and user.sites are no longer available, simply set the current profile to the selected authorization.
      this.currentAuthorizationProfile = authorization;
    });
  }
}
